import { produce } from 'immer'
import { isEqual, uniq } from 'lodash'
import type { Contact } from 'signify-ts'
import { SignifyClient } from 'signify-ts'
import { StateCreator } from 'zustand'

import constants, { FETCHING_STATUS } from '@/config/constants'
import {
  createIdentifier,
  deleteIdentifier,
  generateChallenge,
  getContact,
  getContacts,
  getCredentials,
  getIdentifier,
  getIdentifiers,
  getKeyState,
  getNotifications,
  getOobis,
  getRegistries,
  initClient,
  issueCredential,
  renameIdentifier,
  resolveOobi
} from '@/services/signify'
import { GroupMember } from '@/services/wallet/types'
import { IOOBI, Registries } from '@/types/SignifyTypes'
import type { IStore } from '@/state/store'
import { AuthStatus } from '@/state/authentication'

export enum CLIENT_CONNECTIVITY_STATUS {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR'
}

interface ITask {
  operation: keyof IActions
  payload?: { [key: string]: any }
}

export interface Identifier {
  name: string
  prefix: string
  isMultisig: boolean
  group?: GroupAid
  raw?: any
  type?: string
}

export interface GroupAid {
  name: string
  prefix: string
  // localAid is undefined when the wallet doesn't have a local member of
  // the group (e.g. observer-only views of an external multisig); consumers
  // null-check before reading `.name`/`.prefix`.
  localAid:
    | {
        name: string
        prefix: string
      }
    | undefined
  members?: GroupMember[]
  minSignersThreshold: number
  witnessOobi?: string
}

export interface IChallenge {
  dt?: string
  words: string[]
  said?: string
  authenticated?: boolean
}

export interface IContact {
  id: string
  alias?: string
  oobi?: string
  challenges: IChallenge[]
}

interface registry {
  regk: string
}

export enum KNotificationType {
  ICP = '/multisig/icp',
  IXN = '/multisig/ixn',
  ROT = '/multisig/rot',
  RPY = '/multisig/rpy',
  VCP = '/multisig/vcp',
  ISS = '/multisig/iss',
  EXN = '/multisig/exn',
  GRANT = '/exn/ipex/grant',
  REV = '/multisig/rev',
  ADMIT = '/exn/ipex/admit'
}

interface KNotification {
  i: string
  dt: string
  r: boolean
  a: {
    r: KNotificationType
    d: string
  }
}

interface IState {
  clientConnectivityStatus: CLIENT_CONNECTIVITY_STATUS
  notificationProcessingStatus: string[]
  passCodeLifeTime: number
  passCode: string
  timeoutIDs: {
    signifyTimeOutId: NodeJS.Timeout | null
    toastTimeOutId: NodeJS.Timeout | null
  }
  promptForPassCode: boolean
  forcedClose: boolean
  signifyTaskQueue: ITask[]
  signifyTaskPerformed: (keyof IActions)[]
  signifyClient: SignifyClient | null
  /** True after attachInterceptor installs the signing window.fetch replacement.
   *  Used by makeWaitForReady as a 4th gate condition to prevent unsigned
   *  requests racing out before the interceptor is in place (Security Hawk #2). */
  signifyInterceptorAttached: boolean
  aids: {
    fetchingStatus: FETCHING_STATUS
    identifiers: Identifier[]
  }
  aid: Identifier | null
  userAidHab: any | null
  originAid: string | null
  notifications: {
    fetchingStatus: FETCHING_STATUS
    notifications: {
      start: number
      end: number
      total: number
      notes: KNotification[]
    }
  }
  contacts: {
    fetchingStatus: FETCHING_STATUS
    contacts: IContact[]
  }
  registries: {
    fetchingStatus: FETCHING_STATUS
    registries: registry[]
  }
}

interface IActions {
  connectToAgent: (passCode: string, originAid?: string) => Promise<boolean>
  setClientConnectivityStatus: (
    clientConnectivityStatus: CLIENT_CONNECTIVITY_STATUS
  ) => void
  getIdentifiers: () => Promise<Identifier[]>
  getIdentifierByAlias: (alias: string) => Promise<any>
  getNotifications: (last?: number, limit?: number) => void
  addNotificationProcess: (process: string) => void
  removeNotificationProcess: (process: string) => void
  getContacts: (
    group?: string,
    filterField?: string,
    filterValue?: string
  ) => Promise<IContact[]>
  getContact: (identifier: string) => Promise<any>
  getKeyState: (identifier: string) => void
  getRegistries: (name: string) => Promise<Registries[]>
  getOOBIS: (alias: string, role: string) => Promise<IOOBI>
  resolveOOBIS: (oobi: string, alias: string) => Promise<any>
  issueCredential: (
    alias: string,
    registry: string,
    recipient: string,
    schema: string,
    credentialData: any,
    rules: any | undefined,
    source: any | undefined,
    _private?: boolean
  ) => any
  createIdentifier: (alias: string, witnessAids: string[]) => Promise<any>
  deleteIdentifier: (alias: string) => Promise<any>
  renameIdentifier: (alias: string, newAlias: string) => Promise<any>
  getCredentials: (filter: object) => Promise<any[]>
  generateChallenge: () => Promise<string>
  updateIdentifier: (updatedIdentifier: Identifier) => void
}

export type ISignify = IState &
  IActions & {
    setPassCodeLifeTime: (passCodeLifeTime: number) => void
    showPassCodePrompt: () => void
    hidePassCodePrompt: () => void
    setForcedClose: (value: boolean) => void
    clearSignifyTaskPerformed: () => void
    setSignifyInterceptorAttached: (value: boolean) => void
    clearSignify: () => void
    _passcodePrompter: (
      operation: keyof IActions,
      payload: any,
      cb: () => Promise<any>
    ) => Promise<any>
    resetSignifyStore: () => void
  }

let client: SignifyClient | null = null

/**
 * Assert that the module-level `client` is set and return it.
 * All call sites that pass `client` to helpers go through this getter; reaching
 * a call site implies `_passcodePrompter` only invokes the callback when the
 * store reports CONNECTED, which is set in lockstep with `client` (see
 * connectSignifyAfterPasscode). cd-0009 (signify client lifecycle) covers the
 * residual risk.
 */
function clientOrThrow(): SignifyClient {
  if (client === null) {
    throw new Error(
      'clientOrThrow: no signify client — callers must run inside _passcodePrompter callback when CONNECTED'
    )
  }
  return client
}

const resetTimeOut = (
  set: (
    partial: (state: ISignify) => ISignify | Partial<ISignify>,
    replace?: boolean | undefined
  ) => void,
  get: () => ISignify
) => {
  get().timeoutIDs.signifyTimeOutId &&
    clearTimeout(get().timeoutIDs.signifyTimeOutId as NodeJS.Timeout)
  const newTimeOutId = setTimeout(() => {
    get().clearSignify()
    // console.log('DISCONNECTING THE CLIENT', new Date())
  }, get().passCodeLifeTime)
  set(
    produce((state: ISignify) => {
      state.timeoutIDs.signifyTimeOutId = newTimeOutId
    })
  )

  get().timeoutIDs.toastTimeOutId &&
    clearTimeout(get().timeoutIDs.toastTimeOutId as NodeJS.Timeout)
  const newToastTimeoutId = setTimeout(() => {
    set(
      produce((state: ISignify) => {
        ;((state.signifyTaskPerformed = []),
          (state.timeoutIDs.toastTimeOutId = null))
      })
    )
  }, constants.TOAST_DISPLAY_TIME)
  set(
    produce((state: ISignify) => {
      state.timeoutIDs.toastTimeOutId = newToastTimeoutId
    })
  )
}

export const getPerformedTaskNames = (
  signifyTaskPerformed: (keyof IActions)[]
) => {
  const result: string[] = []
  const operationNameLiteral: { [key in keyof Partial<IActions>]: string } = {
    getIdentifiers: 'Fetch Identifiers',
    getIdentifierByAlias: 'Fetch Identifier by Alias',
    getNotifications: 'Fetch Notification',
    getContacts: 'Fetch Contacts',
    getContact: 'Fetch Contact detail',
    getKeyState: 'Fetch key state',
    getRegistries: 'Fetch registries',
    issueCredential: 'Issue credential',
    createIdentifier: 'Create Identifier'
  }

  uniq(signifyTaskPerformed).forEach((task) => {
    if (task in operationNameLiteral) {
      result.push(operationNameLiteral[task] as string)
    }
  })

  return result
}

const initialState: IState = {
  clientConnectivityStatus: CLIENT_CONNECTIVITY_STATUS.DISCONNECTED,
  notificationProcessingStatus: [],
  passCodeLifeTime: constants.PASS_CODE_LIFETIME,
  passCode: '',
  timeoutIDs: {
    signifyTimeOutId: null,
    toastTimeOutId: null
  },
  promptForPassCode: false,
  forcedClose: false,
  signifyTaskQueue: [],
  signifyTaskPerformed: [],
  signifyInterceptorAttached: false,
  aids: {
    fetchingStatus: FETCHING_STATUS.NOT_INITIATED,
    identifiers: []
  },
  aid: null,
  signifyClient: null,
  userAidHab: null,
  originAid: null,
  notifications: {
    fetchingStatus: FETCHING_STATUS.NOT_INITIATED,
    notifications: {
      start: 0,
      end: 0,
      total: 0,
      notes: []
    }
  },
  contacts: {
    fetchingStatus: FETCHING_STATUS.NOT_INITIATED,
    contacts: []
  },
  registries: {
    fetchingStatus: FETCHING_STATUS.NOT_INITIATED,
    registries: []
  }
}

const signifyStore: StateCreator<IStore, [], [], ISignify> = (set, get) => ({
  ...initialState,
  connectToAgent: async (passCode: string, originAid?: string) => {
    let connected = false
    set(() => ({
      clientConnectivityStatus: CLIENT_CONNECTIVITY_STATUS.CONNECTING
    }))
    try {
      const {
        client: signifyClient,
        userAidHab,
        originAid: resolvedOriginAid
      } = await initClient(passCode, originAid)
      client = signifyClient

      if (!client.agent) {
        const msg = 'No agent found'
        console.error(msg)
        throw new Error(msg)
      }

      set(
        produce((state) => {
          ;((state.clientConnectivityStatus =
            CLIENT_CONNECTIVITY_STATUS.CONNECTED),
            (state.promptForPassCode = false),
            (state.passCode = passCode),
            (state.aids.fetchingStatus = FETCHING_STATUS.NOT_INITIATED))
          state.aids.identifiers = []
          state.signifyClient = client
          state.userAidHab = userAidHab
          state.originAid = resolvedOriginAid
          // Mark the signing interceptor as attached.
          // attachInterceptor(client, userAidHab) was called inside initClient
          // (services/signify.ts); by the time we reach this set() call, the
          // signing window.fetch replacement is in place. Setting this flag
          // here (atomically with CONNECTED) prevents makeWaitForReady from
          // resolving before the interceptor is installed (Security Hawk #2).
          state.signifyInterceptorAttached = true
          // d-9: a first-time connect (no originAid) incepts the user AID at the
          // service level inside initClient (services/signify.ts createIdentifier),
          // which bypasses the store createIdentifier action — the only path that
          // records signifyTaskPerformed. Record it here so the
          // signify--task-performed-toast affordance (d-8) reflects the inception.
          // Re-auth/sign-in pass originAid and skip inception, so don't record it.
          // Also require resolvedOriginAid: initClient swallows inception errors
          // and returns a falsy originAid on failure (e.g. misconfigured
          // witnesses), so gating on the input alone would record completion —
          // and falsely satisfy the e2e seam/toast — for an inception that never
          // happened (PR #886 review).
          if (!originAid && resolvedOriginAid) {
            state.signifyTaskPerformed.push('createIdentifier')
          }
        })
      )

      // cd-0014: drain sequentially (FIFO) — task queue can contain write operations
      // (createIdentifier, resolveOOBIS, issueCredential, …) with inter-task ordering
      // hazards. Parallel drain via Promise.all introduces races. Fail-fast preserved:
      // the first throw aborts the loop and is caught by the outer try/catch.
      // Snapshot the queue before iteration so re-enqueues during drain don't extend the loop.
      const queue = get().signifyTaskQueue.slice()
      for (const task of queue) {
        const params = Object.values(task.payload ?? {})
        // @ts-expect-error: cd-0027 — dynamic dispatch by string key into the ISignify action surface; TS cannot infer arg tuple for arbitrary task.operation
        await get()[task.operation](...params)
      }

      set(
        produce((state: ISignify) => {
          state.signifyTaskQueue = []
        })
      )
      connected = true
      resetTimeOut(set, get)
    } catch (ex) {
      // console.log('ERROR CONNECTING AGENT', ex)
      set(() => ({
        clientConnectivityStatus: CLIENT_CONNECTIVITY_STATUS.ERROR
      }))
    }
    return connected
  },
  setClientConnectivityStatus: (
    clientConnectivityStatus: CLIENT_CONNECTIVITY_STATUS
  ) => {
    set(() => ({ clientConnectivityStatus }))
  },
  showPassCodePrompt: () => {
    set(() => ({ promptForPassCode: true }))
  },
  hidePassCodePrompt: () => {
    set(() => ({ promptForPassCode: false, signifyTaskQueue: [] }))
  },
  setForcedClose(value) {
    set(() => ({ forcedClose: value }))
  },
  clearSignifyTaskPerformed: () => {
    set(() => ({ signifyTaskPerformed: [] }))
  },
  setSignifyInterceptorAttached: (value: boolean) => {
    set(() => ({ signifyInterceptorAttached: value }))
  },
  setPassCodeLifeTime: (passCodeLifeTime) => {
    set(() => ({ passCodeLifeTime }))
  },
  _passcodePrompter: async (operationName, payload, callback) => {
    let result
    if (
      get().clientConnectivityStatus !== CLIENT_CONNECTIVITY_STATUS.CONNECTED
    ) {
      get().showPassCodePrompt()

      const operationAlreadyQueued =
        get().signifyTaskQueue.filter(
          (stq) =>
            stq.operation === operationName && isEqual(stq.payload, payload)
        ).length > 0
      if (!operationAlreadyQueued)
        set(
          produce((state: ISignify) => {
            state.signifyTaskQueue.push({ operation: operationName, payload })
          })
        )
    } else {
      try {
        result = await callback()

        set(
          produce((state: ISignify) => {
            state.signifyTaskPerformed.push(operationName)
          })
        )
        resetTimeOut(set, get)
      } catch (ex) {
        throw ex
      }
    }
    return result
  },
  clearSignify: () => {
    client = null
    // Clear any pending timers before overwriting timeoutIDs with initialState.
    // Without this, the old timer callbacks fire clearSignify() again on the
    // freshly-reset store, overwriting any new state set after this call. (BUG-0032)
    const { signifyTimeOutId, toastTimeOutId } = get().timeoutIDs
    if (signifyTimeOutId !== null) clearTimeout(signifyTimeOutId)
    if (toastTimeOutId !== null) clearTimeout(toastTimeOutId)
    set(initialState)
  },
  getNotifications: async (last, limit) => {
    get()._passcodePrompter('getNotifications', { last, limit }, async () => {
      set(
        produce((_state: ISignify) => {
          _state.notifications.fetchingStatus = FETCHING_STATUS.FETCHING
        })
      )
      const notifications = await getNotifications(clientOrThrow(), last, limit)
      set(
        produce((_state: ISignify) => {
          _state.notifications.notifications = notifications
          _state.notifications.fetchingStatus = FETCHING_STATUS.COMPLETE
          _state.signifyTaskPerformed.push('getNotifications')
        })
      )
    })
  },
  addNotificationProcess: (process: string) => {
    if (!get().notificationProcessingStatus.includes(process))
      set(
        produce((state: ISignify) => {
          state.notificationProcessingStatus = [
            ...state.notificationProcessingStatus,
            process
          ]
        })
      )
  },
  removeNotificationProcess: (process: string) => {
    set(
      produce((state: ISignify) => {
        state.notificationProcessingStatus =
          state.notificationProcessingStatus.filter(
            (notification) => notification !== process
          )
      })
    )
  },
  getIdentifiers: async () => {
    return get()._passcodePrompter('getIdentifiers', {}, async () => {
      set(
        produce((state: ISignify) => {
          state.aids.fetchingStatus = FETCHING_STATUS.FETCHING
        })
      )

      const aids = await getIdentifiers(client as SignifyClient)

      set(
        produce((state: ISignify) => {
          ;((state.aids.fetchingStatus = FETCHING_STATUS.COMPLETE),
            (state.aids.identifiers = aids))
          state.signifyTaskPerformed.push('getIdentifiers')
        })
      )
      return aids
    })
  },
  getIdentifierByAlias: async (alias) => {
    return get()._passcodePrompter(
      'getIdentifierByAlias',
      { alias },
      async () => {
        const identifier = await getIdentifier(clientOrThrow(), alias)
        // console.log('getIdentifierByAlias()', identifier)
        // set(
        //   produce((state: ISignify) => {
        //     state.aid = aid
        //   })
        // )
        return identifier
      }
    )
  },
  getContacts: async (group, filterField, filterValue) => {
    return get()._passcodePrompter(
      'getContacts',
      { group, filterField, filterValue },
      async () => {
        set(
          produce((state: ISignify) => {
            state.contacts.fetchingStatus = FETCHING_STATUS.FETCHING
          })
        )
        const contacts: Contact[] = await getContacts(
          client as SignifyClient,
          group,
          filterField,
          filterValue
        )
        set(
          produce((state: ISignify) => {
            ;((state.contacts.fetchingStatus = FETCHING_STATUS.COMPLETE),
              (state.contacts.contacts = contacts.map((c) => {
                return { challenges: [], ...c }
              })))
            state.signifyTaskPerformed.push('getContacts')
          })
        )
        return contacts
      }
    )
  },
  getContact: async (identifier) => {
    return get()._passcodePrompter('getContact', { identifier }, async () => {
      return await getContact(clientOrThrow(), identifier)
    })
  },
  getKeyState: async (identifier) => {
    return get()._passcodePrompter('getKeyState', { identifier }, async () => {
      return await getKeyState(clientOrThrow(), identifier)
    })
  },
  getRegistries: async (name) => {
    return get()._passcodePrompter('getKeyState', { name }, async () => {
      let regestries: Registries[] = []
      try {
        set(
          produce((state: ISignify) => {
            state.registries.fetchingStatus = FETCHING_STATUS.FETCHING
          })
        )
        regestries = (await getRegistries(
          clientOrThrow(),
          name
        )) as unknown as Registries[]
        set(
          produce((state: ISignify) => {
            ;((state.registries.fetchingStatus = FETCHING_STATUS.COMPLETE),
              (state.registries.registries = regestries))
          })
        )
      } catch (ex) {
        set(
          produce((state: ISignify) => {
            state.registries.fetchingStatus = FETCHING_STATUS.FAILED
          })
        )
      }
      return regestries
    })
  },
  getOOBIS: async (alias, role) => {
    return get()._passcodePrompter('getOOBIS', { alias, role }, async () => {
      return await getOobis(clientOrThrow(), alias, role)
    })
  },
  resolveOOBIS: async (oobi, alias) => {
    return get()._passcodePrompter(
      'resolveOOBIS',
      { oobi, alias },
      async () => {
        return resolveOobi(clientOrThrow(), oobi, alias)
      }
    )
  },
  issueCredential: async (
    alias,
    registry,
    recipient,
    schema,
    credentialData,
    rules,
    source,
    _private
  ) => {
    return get()._passcodePrompter(
      'issueCredential',
      {
        alias,
        registry,
        recipient,
        schema,
        credentialData,
        rules,
        source,
        _private
      },
      async () => {
        return await issueCredential(
          clientOrThrow(),
          alias,
          registry,
          recipient,
          schema,
          credentialData,
          rules,
          source,
          _private
        )
      }
    )
  },
  createIdentifier: async (alias, witnessAids) => {
    return get()._passcodePrompter('createIdentifier', { alias }, async () => {
      const createResp = await createIdentifier(
        clientOrThrow(),
        alias,
        witnessAids
      )
      await get().getIdentifiers()
      return createResp
    })
  },
  deleteIdentifier: async (alias) => {
    return get()._passcodePrompter('deleteIdentifier', { alias }, async () => {
      const resp = await deleteIdentifier(clientOrThrow(), alias)
      await get().getIdentifiers()
      return resp
    })
  },
  renameIdentifier: async (alias, newAlias) => {
    return get()._passcodePrompter(
      'renameIdentifier',
      { alias, newAlias },
      async () => {
        const resp = await renameIdentifier(clientOrThrow(), alias, newAlias)
        await get().getIdentifiers()
        return resp
      }
    )
  },
  getCredentials: async (filter) => {
    return get()._passcodePrompter('getCredentials', { filter }, async () => {
      return await getCredentials(clientOrThrow(), filter)
    })
  },
  generateChallenge: async () => {
    const response = await generateChallenge(clientOrThrow())
    if (response) return response?.words?.join(' ')
    else throw new Error('An error occurred while generating challenge')
  },
  resetSignifyStore: () => {
    set(initialState)
  },
  updateIdentifier(updatedIdentifier) {
    const identifiers = get().aids.identifiers.filter(
      (x) => x.prefix !== updatedIdentifier.prefix
    )
    set(
      produce((state: ISignify) => {
        state.aids.identifiers = [...identifiers, updatedIdentifier]
      })
    )
  }
})

export default signifyStore
